import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChevronDown, Loader2, HelpCircle, Phone, ArrowRight, Search } from "lucide-react";
import { SEO } from "../components/SEO";
import { BIZ } from "../lib/constants";
import { api } from "../lib/api";

// strip markdown for JSON-LD answers (search engines want plain text)
const plain = (md = "") =>
    md
        .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
        .replace(/[*_`>#-]+/g, "")
        .replace(/\s+/g, " ")
        .trim();

// Escape user input before building a regex
const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// Highlight matched substring with <mark>
const HighlightedText = ({ text, query }) => {
    if (!query) return text;
    const trimmed = query.trim();
    if (!trimmed) return text;
    const re = new RegExp(`(${escapeRegex(trimmed)})`, "ig");
    const parts = String(text).split(re);
    const needle = trimmed.toLowerCase();
    return parts.map((part, i) =>
        part.toLowerCase() === needle ? (
            <mark key={i} className="faq-mark">
                {part}
            </mark>
        ) : (
            <React.Fragment key={i}>{part}</React.Fragment>
        )
    );
};

export default function FAQ() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState("All");
    const [openId, setOpenId] = useState(null);
    const [query, setQuery] = useState("");
    const itemRefs = useRef({});

    useEffect(() => {
        api.faqs()
            .then((d) => setItems(d.items || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, []);

    // Scroll-spy: when a FAQ is opened, smooth-scroll it into view (offset for sticky navbar)
    useEffect(() => {
        if (!openId) return;
        const el = itemRefs.current[openId];
        if (el && typeof el.getBoundingClientRect === "function") {
            const top = el.getBoundingClientRect().top + window.scrollY - 100;
            window.scrollTo({ top, behavior: "smooth" });
        }
    }, [openId]);

    // Build a quick index for related-question lookup (same category, exclude current)
    const relatedByCategory = useMemo(() => {
        const idx = {};
        for (const f of items) {
            idx[f.category] = idx[f.category] || [];
            idx[f.category].push(f);
        }
        return idx;
    }, [items]);

    const getRelated = (current) => {
        const pool = (relatedByCategory[current.category] || []).filter((f) => f.id !== current.id);
        // simple deterministic shuffle by id hash to avoid jitter
        return pool.slice(0, 3);
    };

    const categories = useMemo(() => {
        const set = new Set(items.map((f) => f.category));
        return ["All", ...Array.from(set)];
    }, [items]);

    const visible = useMemo(() => {
        let list = active === "All" ? items : items.filter((f) => f.category === active);
        const q = query.trim().toLowerCase();
        if (q) {
            list = list.filter(
                (f) =>
                    (f.question || "").toLowerCase().includes(q) ||
                    (f.answer || "").toLowerCase().includes(q)
            );
        }
        return list;
    }, [items, active, query]);

    const jsonLd = useMemo(() => {
        if (!items.length) return null;
        return {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: items.map((f) => ({
                "@type": "Question",
                name: f.question,
                acceptedAnswer: {
                    "@type": "Answer",
                    text: plain(f.answer),
                },
            })),
        };
    }, [items]);

    return (
        <>
            <SEO
                title="Frequently Asked Questions"
                description="Answers to common questions about Liquor Studio in Clinton, Missouri — hours, location, special orders, allocated bourbon, Missouri liquor law, weddings, gifting, and more."
                path="/faq"
                jsonLd={jsonLd}
            />

            {/* Header */}
            <section
                style={{
                    paddingTop: 160,
                    paddingBottom: 64,
                    borderBottom: "1px solid var(--ls-border)",
                }}
                data-testid="faq-header"
            >
                <div className="ls-container">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="max-w-3xl"
                    >
                        <span className="eyebrow">FAQ</span>
                        <h1
                            className="font-display mt-5 text-white"
                            style={{
                                fontSize: "clamp(44px, 7vw, 84px)",
                                fontWeight: 500,
                                lineHeight: 1.02,
                                letterSpacing: "-0.02em",
                            }}
                        >
                            Frequently<br />
                            <em style={{ fontStyle: "italic", color: "var(--ls-gold)", fontWeight: 400 }}>
                                asked questions.
                            </em>
                        </h1>
                        <p
                            className="mt-6"
                            style={{
                                color: "var(--ls-text-secondary)",
                                fontSize: 18,
                                lineHeight: 1.7,
                                maxWidth: 620,
                            }}
                        >
                            Everything you wanted to know about Liquor Studio — hours, special
                            orders, allocated bourbon, weddings, Missouri liquor law, and more.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Body */}
            <section className="section-y" data-testid="faq-content">
                <div className="ls-container">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        {/* Category sidebar */}
                        <aside className="lg:col-span-3" data-testid="faq-categories">
                            <div className="lg:sticky" style={{ top: 100 }}>
                                <span className="eyebrow">Categories</span>
                                <ul className="mt-5 flex lg:flex-col gap-2 flex-wrap" role="tablist" aria-label="Filter FAQs by category">
                                    {loading ? (
                                        <li style={{ color: "var(--ls-text-muted)" }}>
                                            <Loader2 size={14} className="animate-spin inline mr-2" />
                                            Loading…
                                        </li>
                                    ) : (
                                        categories.map((c) => {
                                            const isActive = active === c;
                                            return (
                                                <li key={c}>
                                                    <button
                                                        role="tab"
                                                        aria-selected={isActive}
                                                        onClick={() => {
                                                            setActive(c);
                                                            setOpenId(null);
                                                        }}
                                                        className="text-sm font-medium"
                                                        style={{
                                                            padding: "10px 14px",
                                                            borderRadius: 6,
                                                            background: isActive ? "rgba(245,176,39,0.10)" : "transparent",
                                                            color: isActive ? "var(--ls-gold)" : "var(--ls-text-secondary)",
                                                            border: `1px solid ${isActive ? "rgba(245,176,39,0.4)" : "var(--ls-border)"}`,
                                                            display: "block",
                                                            width: "100%",
                                                            textAlign: "left",
                                                            cursor: "pointer",
                                                            transition: "all 0.2s ease",
                                                        }}
                                                        data-testid={`faq-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
                                                    >
                                                        {c}
                                                    </button>
                                                </li>
                                            );
                                        })
                                    )}
                                </ul>

                                <div
                                    className="mt-10 hidden lg:block"
                                    style={{
                                        background: "var(--ls-panel)",
                                        border: "1px solid var(--ls-border)",
                                        borderRadius: 8,
                                        padding: 22,
                                    }}
                                >
                                    <HelpCircle size={20} style={{ color: "var(--ls-gold)" }} />
                                    <h3
                                        className="font-display text-white mt-3"
                                        style={{ fontSize: 18, fontWeight: 500 }}
                                    >
                                        Still have a question?
                                    </h3>
                                    <p
                                        className="mt-2 text-xs"
                                        style={{ color: "var(--ls-text-secondary)", lineHeight: 1.6 }}
                                    >
                                        We answer fast. Call or drop us a note.
                                    </p>
                                    <div className="mt-4 flex flex-col gap-2">
                                        <a
                                            href={`tel:${BIZ.phoneTel}`}
                                            className="btn-outline-gold"
                                            style={{ fontSize: 12, padding: "8px 14px", justifyContent: "center" }}
                                            data-testid="faq-sidebar-call"
                                        >
                                            <Phone size={12} /> {BIZ.phone}
                                        </a>
                                        <Link
                                            to="/contact"
                                            className="btn-ghost"
                                            style={{ fontSize: 12, padding: "8px 14px", justifyContent: "center" }}
                                            data-testid="faq-sidebar-contact"
                                        >
                                            Contact Form
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </aside>

                        {/* Accordion */}
                        <div className="lg:col-span-9">
                            {/* Search */}
                            <div
                                className="mb-6"
                                style={{ position: "relative" }}
                                data-testid="faq-search-wrap"
                            >
                                <label htmlFor="faq-search" className="sr-only">
                                    Search FAQs
                                </label>
                                <Search
                                    size={16}
                                    style={{
                                        position: "absolute",
                                        left: 18,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        color: "var(--ls-text-muted)",
                                        pointerEvents: "none",
                                    }}
                                    aria-hidden="true"
                                />
                                <input
                                    id="faq-search"
                                    type="search"
                                    placeholder="Search by keyword — e.g. hours, wedding, bourbon…"
                                    value={query}
                                    onChange={(e) => {
                                        setQuery(e.target.value);
                                        setOpenId(null);
                                    }}
                                    className="ls-input"
                                    style={{ paddingLeft: 46, fontSize: 15 }}
                                    data-testid="faq-search-input"
                                    aria-controls="faq-list"
                                />
                                {query && (
                                    <button
                                        type="button"
                                        onClick={() => setQuery("")}
                                        aria-label="Clear search"
                                        className="text-xs"
                                        style={{
                                            position: "absolute",
                                            right: 14,
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            background: "transparent",
                                            border: "1px solid var(--ls-border-strong)",
                                            color: "var(--ls-text-secondary)",
                                            borderRadius: 999,
                                            padding: "4px 10px",
                                            cursor: "pointer",
                                        }}
                                        data-testid="faq-search-clear"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>

                            <p
                                className="text-xs mb-4"
                                style={{ color: "var(--ls-text-muted)" }}
                                aria-live="polite"
                                data-testid="faq-result-count"
                            >
                                {loading
                                    ? ""
                                    : query.trim()
                                    ? `Showing ${visible.length} result${visible.length === 1 ? "" : "s"} for "${query.trim()}"`
                                    : `${visible.length} question${visible.length === 1 ? "" : "s"}${active !== "All" ? ` in ${active}` : ""}`}
                            </p>

                            {loading ? (
                                <div
                                    className="text-center py-20"
                                    style={{ color: "var(--ls-text-secondary)" }}
                                    data-testid="faq-loading"
                                >
                                    <Loader2 size={20} className="animate-spin inline-block mr-2" />
                                    Loading questions…
                                </div>
                            ) : visible.length === 0 ? (
                                <div
                                    className="text-center py-16"
                                    data-testid="faq-empty"
                                    style={{
                                        background: "var(--ls-panel)",
                                        border: "1px dashed var(--ls-border-strong)",
                                        borderRadius: 8,
                                        padding: 36,
                                    }}
                                >
                                    <Search size={22} style={{ color: "var(--ls-text-muted)", margin: "0 auto" }} />
                                    <p
                                        className="mt-4 text-sm"
                                        style={{ color: "var(--ls-text-secondary)", lineHeight: 1.6 }}
                                    >
                                        {query.trim() ? (
                                            <>
                                                No matching FAQs — try a different keyword, or{" "}
                                                <Link
                                                    to="/contact"
                                                    className="link-underline text-white"
                                                    data-testid="faq-empty-contact-link"
                                                >
                                                    contact us
                                                </Link>
                                                .
                                            </>
                                        ) : (
                                            <>No questions in this category yet.</>
                                        )}
                                    </p>
                                </div>
                            ) : (
                                <ul
                                    id="faq-list"
                                    className="flex flex-col gap-3"
                                    data-testid="faq-list"
                                >
                                    {visible.map((f, i) => {
                                        const isOpen = openId === f.id;
                                        return (
                                            <li
                                                key={f.id}
                                                ref={(el) => { itemRefs.current[f.id] = el; }}
                                                style={{
                                                    background: "var(--ls-panel)",
                                                    border: `1px solid ${isOpen ? "var(--ls-border-strong)" : "var(--ls-border)"}`,
                                                    borderRadius: 8,
                                                    overflow: "hidden",
                                                    transition: "border-color 0.2s ease",
                                                }}
                                                data-testid={`faq-item-${i}`}
                                            >
                                                <button
                                                    onClick={() => setOpenId(isOpen ? null : f.id)}
                                                    aria-expanded={isOpen}
                                                    aria-controls={`faq-answer-${f.id}`}
                                                    style={{
                                                        background: "transparent",
                                                        border: "none",
                                                        width: "100%",
                                                        textAlign: "left",
                                                        padding: "22px 26px",
                                                        display: "flex",
                                                        gap: 20,
                                                        alignItems: "center",
                                                        cursor: "pointer",
                                                    }}
                                                    data-testid={`faq-question-${i}`}
                                                >
                                                    <span
                                                        className="text-white"
                                                        style={{ flex: 1, fontSize: 17, fontWeight: 500, lineHeight: 1.4 }}
                                                    >
                                                        <HighlightedText text={f.question} query={query} />
                                                    </span>
                                                    <span
                                                        style={{
                                                            color: isOpen ? "var(--ls-gold)" : "var(--ls-text-muted)",
                                                            transition: "transform 0.25s ease, color 0.2s ease",
                                                            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        <ChevronDown size={20} />
                                                    </span>
                                                </button>
                                                <AnimatePresence initial={false}>
                                                    {isOpen && (
                                                        <motion.div
                                                            id={`faq-answer-${f.id}`}
                                                            role="region"
                                                            initial={{ height: 0, opacity: 0 }}
                                                            animate={{ height: "auto", opacity: 1 }}
                                                            exit={{ height: 0, opacity: 0 }}
                                                            transition={{ duration: 0.25 }}
                                                            style={{ overflow: "hidden" }}
                                                            data-testid={`faq-answer-${i}`}
                                                        >
                                                            <div
                                                                className="faq-answer-prose"
                                                                style={{
                                                                    padding: "0 26px 26px",
                                                                    color: "var(--ls-text-secondary)",
                                                                    fontSize: 15,
                                                                    lineHeight: 1.75,
                                                                }}
                                                            >
                                                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                                    {f.answer}
                                                                </ReactMarkdown>
                                                                {/* Related questions — Phase 6 */}
                                                                {(() => {
                                                                    const rel = getRelated(f);
                                                                    if (rel.length === 0) return null;
                                                                    return (
                                                                        <div
                                                                            className="mt-6 pt-4"
                                                                            style={{ borderTop: "1px dashed var(--ls-border-strong)" }}
                                                                            data-testid={`faq-related-${i}`}
                                                                        >
                                                                            <span className="eyebrow" style={{ fontSize: 10, letterSpacing: "0.24em" }}>Related questions</span>
                                                                            <ul style={{ marginTop: 12, listStyle: "none", padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                                                                                {rel.map((r, j) => (
                                                                                    <li key={r.id}>
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => setOpenId(r.id)}
                                                                                            className="link-underline"
                                                                                            style={{
                                                                                                background: "transparent",
                                                                                                border: "none",
                                                                                                color: "var(--ls-text)",
                                                                                                fontSize: 14,
                                                                                                textAlign: "left",
                                                                                                cursor: "pointer",
                                                                                                padding: 0,
                                                                                                fontFamily: "inherit",
                                                                                            }}
                                                                                            data-testid={`faq-related-link-${i}-${j}`}
                                                                                        >
                                                                                            → {r.question}
                                                                                        </button>
                                                                                    </li>
                                                                                ))}
                                                                            </ul>
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </div>
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}

                            <div
                                className="mt-12"
                                style={{
                                    background: "linear-gradient(180deg, var(--ls-panel) 0%, #0e0e0e 100%)",
                                    border: "1px solid var(--ls-border)",
                                    borderRadius: 8,
                                    padding: 36,
                                }}
                            >
                                <h3
                                    className="font-display text-white"
                                    style={{ fontSize: 26, fontWeight: 500 }}
                                >
                                    Didn't find your answer?
                                </h3>
                                <p
                                    className="mt-3"
                                    style={{ color: "var(--ls-text-secondary)", fontSize: 15, lineHeight: 1.6 }}
                                >
                                    We answer phones and emails within one business day, usually
                                    a few hours.
                                </p>
                                <div className="mt-6 flex flex-wrap gap-3">
                                    <Link
                                        to="/contact"
                                        className="btn-primary"
                                        style={{ fontSize: 13, padding: "12px 22px" }}
                                        data-testid="faq-contact-cta"
                                    >
                                        Send Us a Message <ArrowRight size={13} />
                                    </Link>
                                    <a
                                        href={`tel:${BIZ.phoneTel}`}
                                        className="btn-outline-gold"
                                        style={{ fontSize: 13, padding: "12px 22px" }}
                                        data-testid="faq-call-cta"
                                    >
                                        <Phone size={13} /> {BIZ.phone}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
